import { useState, useMemo, useEffect } from 'react';
import { Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axios from 'axios';
import { Dialog } from '../../../../components/Dialog';
import { RxOptions } from '../RxOptions';
import { FilterSelector } from '../../../../components/FilterSelector';
import * as api from '../../../../lib/api';
import { DownloadButton } from '../../../../components/DownloadButton';
import { useContext } from '../../context.jsx';
import { useFourRContext } from './four-r-context.jsx';
import { useCurrentUser } from '../../../../hooks/use-current-user';
import { useGrowers } from '../../../../hooks/use-growers.js';

export function FourRDialog() {
  const user = useCurrentUser();
  const [options, setOptions] = useState();
  const [contacts, setContacts] = useState([]);
  const [ccas, setCcas] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCca, setSelectedCca] = useState(null);
  const [isSaving, setIsSaving] = useState();
  const { isOpen, setOpen } = useFourRContext();
  const { getSelectedPrescriptionIds } = useContext();
  const { selectedId: growerId } = useGrowers();

  useEffect(() => {
    if (isOpen)
      api.get(`/farmgate/contacts/${growerId}`).then((results) => {
        setContacts(results);
        if (results?.length === 1) setSelectedContact(results[0]);
      });
  }, [growerId, isOpen]);

  useEffect(() => {
    if (isOpen)
      api.get(`/users/ccas`).then((results) => {
        const groupedMultiStateLicenses = Object.values(
          results.reduce((acc, curUser) => {
            const { licenseNumber, state } = curUser;
            if (acc[licenseNumber]) {
              acc[licenseNumber].state += `, ${state}`;
            } else {
              acc[licenseNumber] = curUser;
            }
            return acc;
          }, {}),
        );

        if (results.length) setCcas(groupedMultiStateLicenses);
      });
  }, [isOpen, user]);

  const isEmailEnabled = useMemo(
    () => selectedContact?.email,
    [selectedContact],
  );

  const isCCASelectVisible = useMemo(
    () =>
      ccas.length &&
      !ccas.map(({ username }) => username).includes(user.username),
    [ccas, user],
  );

  const leadAgronomists = useMemo(() => {
    const leadAgronomistUsernames = ['tkimmel', 'agault', 'demmert'];

    return ccas.filter((cca) => leadAgronomistUsernames.includes(cca.username));
  }, [ccas]);

  function save4R(sendElectronically) {
    const payload = {
      operationAgronomistId: selectedContact.operationAgronomistId,
      contactId: selectedContact.contactId,
      sendElectronically,
      rxTypes: options.elements,
      ids: getSelectedPrescriptionIds(options.elements),
      selectedCca,
    };

    return api.post(`/growers/${growerId}/lpk/nutrient`, payload);
  }

  async function downloadPdf() {
    const pdf = await save4R(false);

    const res = await axios.get(pdf.signaturePdfUrl, {
      responseType: 'blob',
    });

    res.filename = pdf.filename;

    return res;
  }

  if (!isOpen) return null;

  return (
    <Dialog
      keepMounted
      open
      width="xs"
      title="4R Signature Page"
      onCancel={() => setOpen(false)}
      actions={buildActions()}
    >
      <RxOptions setOptions={setOptions} />
      <FilterSelector
        sx={{ mt: 3, ml: '0 !important', width: '100%' }}
        isVisible={true}
        selectedOption={selectedContact}
        options={contacts}
        onChange={(selected) => setSelectedContact(selected)}
        sortKeys={['contactName']}
        optionLabelKey="contactName"
        optionKey="contactId"
        optionContentKey="contactName"
        inputLabel="Select Operation Contact"
        dark={false}
        required
      />
      <FilterSelector
        sx={{ mt: 3, ml: '0 !important', width: '100%' }}
        isVisible={isCCASelectVisible}
        selectedOption={selectedCca}
        options={leadAgronomists}
        onChange={(selected) => setSelectedCca(selected)}
        sortKeys={['name']}
        optionLabelKey="name"
        optionKey="id"
        optionContentKey="name"
        inputLabel="Select CCA"
        dark={false}
        required
      />
    </Dialog>
  );

  function buildActions() {
    return [
      <Button
        onClick={(event) => {
          event.stopPropagation();
          setOpen(false);
        }}
        data-testid={'cancel-selection'}
        variant="contained"
        color="dark"
        key="cancel-btn"
      >
        Cancel
      </Button>,
      isCCASelectVisible && (
        <LoadingButton
          key="sendToCca"
          variant="contained"
          disabled={
            !options?.elements?.length || !contacts.length || !selectedCca
          }
          loading={isSaving}
          onClick={() => {
            setIsSaving(true);
            save4R(false).finally(() => {
              setIsSaving(false);
              setOpen(false);
            });
          }}
        >
          Send to CCA
        </LoadingButton>
      ),
      !isCCASelectVisible && (
        <DownloadButton
          key="download"
          variant="contained"
          disabled={!options?.elements?.length || !contacts.length}
          download={downloadPdf}
        >
          Download
        </DownloadButton>
      ),
      !isCCASelectVisible && (
        <LoadingButton
          key="email"
          variant="contained"
          color="primary"
          disabled={!options?.elements?.length || !isEmailEnabled}
          loading={isSaving}
          onClick={() => {
            setIsSaving(true);
            save4R(true).finally(() => {
              setIsSaving(false);
              setOpen(false);
            });
          }}
        >
          Email
        </LoadingButton>
      ),
    ].filter(Boolean);
  }
}
